<template>
  <div
    class="slide-viewer-container"
    :class="{ 'is-fullscreen': isFullscreen, 'is-pdf-capturing': isPdfCapturing }"
    @keydown="handleKeydown"
    tabindex="0"
    ref="viewerContainer"
  >
    <!-- Slide Aspect Ratio Container -->
    <div class="slide-aspect-ratio" ref="aspectRatio">
      <div class="slide-viewport" :class="{ 'pdf-capture-mode': isPdfCapturing }" :style="scaleStyle">
        <transition :name="isPdfCapturing ? '' : slideTransition" mode="out-in">
          <quarterly-slide-factory
            :key="currentSlide.id"
            :slide-data="currentSlide"
          />
        </transition>
      </div>
    </div>

    <!-- Navigation Controls -->
    <quarterly-slide-controls
      :current-index="currentIndex"
      :total-slides="slides.length"
      :is-fullscreen="isFullscreen"
      :is-generating-pdf="isGeneratingPdf"
      @prev="navigatePrev"
      @next="navigateNext"
      @goto="navigateToSlide"
      @fullscreen="$emit('fullscreen')"
      @download-pdf="$emit('download-pdf')"
    />

    <!-- Keyboard Shortcuts Hint -->
    <div class="keyboard-hints" v-if="showHints">
      <span><kbd>&larr;</kbd> <kbd>&rarr;</kbd> {{ $t('reports.quarterly.viewer.navigate') }}</span>
      <span><kbd>F</kbd> {{ $t('reports.quarterly.viewer.fullscreen') }}</span>
      <span><kbd>Esc</kbd> {{ $t('reports.quarterly.viewer.exit') }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuarterlySlideViewer',
  components: {
    'quarterly-slide-factory': () => import('./QuarterlySlideFactory.vue'),
    'quarterly-slide-controls': () => import('./QuarterlySlideControls.vue')
  },
  props: {
    slides: {
      type: Array,
      required: true,
      default: () => []
    },
    currentIndex: {
      type: Number,
      default: 0
    },
    isFullscreen: {
      type: Boolean,
      default: false
    },
    isGeneratingPdf: {
      type: Boolean,
      default: false
    },
    isPdfCapturing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      containerWidth: 0,
      slideTransition: 'slide-fade',
      showHints: true,
      touchStartX: 0
    };
  },
  computed: {
    currentSlide() {
      return this.slides[this.currentIndex] || { id: 'empty', type: 'Empty', data: {} };
    },
    scaleStyle() {
      // During PDF capture, use scale(1) to capture at full resolution
      if (this.isPdfCapturing) {
        return {
          transform: 'scale(1)',
          transformOrigin: 'top left'
        };
      }
      // Base slide dimensions (16:9)
      const baseWidth = 1920;
      const scale = this.containerWidth / baseWidth;
      return {
        transform: `scale(${Math.min(scale, 1)})`,
        transformOrigin: 'top left'
      };
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateContainerWidth();
      if (this.$refs.viewerContainer) {
        this.$refs.viewerContainer.focus();
      }
    });
    window.addEventListener('resize', this.updateContainerWidth);

    // Touch events for swipe
    if (this.$refs.viewerContainer) {
      this.$refs.viewerContainer.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.$refs.viewerContainer.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    }

    // Hide hints after 5 seconds
    setTimeout(() => {
      this.showHints = false;
    }, 5000);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateContainerWidth);
    if (this.$refs.viewerContainer) {
      this.$refs.viewerContainer.removeEventListener('touchstart', this.handleTouchStart);
      this.$refs.viewerContainer.removeEventListener('touchend', this.handleTouchEnd);
    }
  },
  watch: {
    isFullscreen(newVal) {
      this.$nextTick(() => {
        this.updateContainerWidth();
        if (newVal && this.$refs.viewerContainer) {
          this.$refs.viewerContainer.focus();
        }
      });
    }
  },
  methods: {
    updateContainerWidth() {
      if (this.$refs.aspectRatio) {
        this.containerWidth = this.$refs.aspectRatio.clientWidth;
      }
    },
    handleKeydown(e) {
      const keyActions = {
        'ArrowRight': () => this.navigateNext(),
        ' ': () => this.navigateNext(),
        'ArrowLeft': () => this.navigatePrev(),
        'Home': () => this.navigateToSlide(0),
        'End': () => this.navigateToSlide(this.slides.length - 1),
        'f': () => this.$emit('fullscreen'),
        'F': () => this.$emit('fullscreen'),
        'Escape': () => {
          if (this.isFullscreen) {
            this.$emit('fullscreen');
          }
        }
      };

      if (keyActions[e.key]) {
        e.preventDefault();
        keyActions[e.key]();
      }

      // Number keys 1-9 for quick navigation
      if (/^[1-9]$/.test(e.key)) {
        const slideIndex = parseInt(e.key) - 1;
        if (slideIndex < this.slides.length) {
          this.navigateToSlide(slideIndex);
        }
      }
    },
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
    },
    handleTouchEnd(e) {
      const touchEndX = e.changedTouches[0].clientX;
      const diff = this.touchStartX - touchEndX;

      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.navigateNext();
        } else {
          this.navigatePrev();
        }
      }
    },
    navigatePrev() {
      this.slideTransition = 'slide-fade-reverse';
      this.$emit('navigate', 'prev');
    },
    navigateNext() {
      this.slideTransition = 'slide-fade';
      this.$emit('navigate', 'next');
    },
    navigateToSlide(index) {
      this.slideTransition = index > this.currentIndex ? 'slide-fade' : 'slide-fade-reverse';
      this.$emit('navigate', index);
    }
  }
};
</script>

<style lang="scss" scoped>
.slide-viewer-container {
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  outline: none;

  &.is-fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    max-width: none;
    z-index: 9999;
    background: #0f0f0f;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 24px;
    box-sizing: border-box;

    .slide-aspect-ratio {
      max-height: calc(100vh - 120px);
    }

    // Fullscreen controls styling
    ::v-deep .slide-controls {
      position: absolute;
      bottom: 24px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(8px);
    }
  }
}

.slide-aspect-ratio {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; // 16:9 ratio
  overflow: hidden;
  background: #fafafa;
  border-radius: 12px;
  box-shadow:
    0 4px 12px -2px rgba(0, 0, 0, 0.1),
    0 8px 24px -4px rgba(0, 0, 0, 0.15);
}

// PDF capture mode - remove clipping and set full dimensions
.slide-viewer-container.is-pdf-capturing {
  .slide-aspect-ratio {
    overflow: visible;
    padding-bottom: 0;
    width: 1920px;
    height: 1080px;
  }

  .slide-viewport {
    position: relative;
  }
}

.slide-viewport {
  position: absolute;
  top: 0;
  left: 0;
  width: 1920px;
  height: 1080px;
}

// Slide transitions
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-enter {
  opacity: 0;
  transform: translateX(30px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-fade-reverse-enter-active,
.slide-fade-reverse-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-reverse-enter {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-fade-reverse-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

// Keyboard hints
.keyboard-hints {
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 24px;
  padding: 12px 24px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 8px;
  font-size: 12px;
  color: #fff;
  animation: fadeIn 0.3s ease-out, fadeOut 0.3s ease-out 4.7s forwards;

  kbd {
    display: inline-block;
    padding: 2px 6px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    margin-right: 4px;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .slide-viewer-container {
    &.is-fullscreen {
      padding: 12px;
    }
  }

  .keyboard-hints {
    display: none;
  }
}
</style>
